import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Image, 
  FolderOpen, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  Plus,
  Edit,
  Trash2,
  Eye,
  Upload,
  RefreshCw,
  Users,
  Key,
  Shield,
  ImageIcon,
  ShoppingCart,
  MessageSquare,
  Package,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  Mail,
  MailOpen
} from 'lucide-react';
import { 
  collectionsAPI, 
  artworksAPI, 
  blogAPI, 
  settingsAPI, 
  statsAPI,
  uploadAPI,
  usersAPI,
  mediaAPI,
  ordersAPI,
  quotesAPI,
  messagesAPI,
  seedDatabase 
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import ImageUpload from '../components/admin/ImageUpload';
import { getImageUrl } from '../utils/imageUrl';

const AdminPage = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const sidebarLinks = [
    { id: 'dashboard', name: 'Dashboard', icon: LayoutDashboard },
    { id: 'orders', name: 'Orders', icon: ShoppingCart },
    { id: 'quotes', name: 'Quote Requests', icon: MessageSquare },
    { id: 'messages', name: 'Messages', icon: Mail },
    { id: 'artworks', name: 'Artworks', icon: Image },
    { id: 'collections', name: 'Collections', icon: FolderOpen },
    { id: 'blog', name: 'Blog Posts', icon: FileText },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'settings', name: 'Settings', icon: Settings },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardContent />;
      case 'orders':
        return <OrdersContent />;
      case 'quotes':
        return <QuotesContent />;
      case 'messages':
        return <MessagesContent />;
      case 'artworks':
        return <ArtworksContent />;
      case 'collections':
        return <CollectionsContent />;
      case 'blog':
        return <BlogContent />;
      case 'users':
        return <UsersContent />;
      case 'settings':
        return <SettingsContent />;
      default:
        return <DashboardContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <span className="text-xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
            gorit<span className="relative">o<span className="absolute -top-1 -right-1 text-[6px]">°</span></span>
          </span>
          <span className="text-xs text-gray-500 ml-1">Admin</span>
        </Link>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2">
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <div className="h-full flex flex-col">
            {/* Logo */}
            <div className="p-6 border-b border-gray-100">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-light" style={{ fontFamily: 'Georgia, serif' }}>
                  gorit<span className="relative">o<span className="absolute -top-1 -right-1 text-[8px]">°</span></span>
                </span>
                <span className="text-xs text-gray-500 ml-2 bg-gray-100 px-2 py-1 rounded">Admin</span>
              </Link>
            </div>

            {/* Navigation */}
            <nav className="flex-1 p-4">
              <ul className="space-y-1">
                {sidebarLinks.map((link) => (
                  <li key={link.id}>
                    <button
                      onClick={() => {
                        setActiveTab(link.id);
                        setSidebarOpen(false);
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm transition-colors ${
                        activeTab === link.id
                          ? 'bg-black text-white'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <link.icon className="w-5 h-5" />
                      {link.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 space-y-2">
              {user && (
                <div className="px-4 py-2 text-xs text-gray-500">
                  Logged in as <span className="font-medium text-gray-700">{user.username}</span>
                </div>
              )}
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-2 text-sm text-gray-600 hover:text-black transition-colors"
              >
                <Eye className="w-5 h-5" />
                View Site
              </Link>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Sign Out
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8">
          {renderContent()}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

// Dashboard Content
const DashboardContent = () => {
  const [stats, setStats] = useState({ artworks: 0, collections: 0, blog_posts: 0, orders: 0, pending_orders: 0, new_quotes: 0, views: '0' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const data = await statsAPI.get();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSeed = async () => {
    try {
      await seedDatabase();
      loadStats();
      alert('Database seeded successfully!');
    } catch (error) {
      console.error('Error seeding database:', error);
    }
  };

  const statItems = [
    { label: 'Total Orders', value: stats.orders, icon: ShoppingCart, highlight: stats.pending_orders > 0 },
    { label: 'Pending Orders', value: stats.pending_orders, icon: Clock, highlight: stats.pending_orders > 0 },
    { label: 'Quote Requests', value: stats.new_quotes, icon: MessageSquare, highlight: stats.new_quotes > 0 },
    { label: 'Total Artworks', value: stats.artworks, icon: Image },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
          Dashboard
        </h1>
        <button 
          onClick={handleSeed}
          className="flex items-center gap-2 px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Seed Data
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statItems.map((stat) => (
          <div key={stat.label} className={`bg-white rounded-lg p-6 shadow-sm ${stat.highlight ? 'ring-2 ring-amber-400' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <stat.icon className={`w-8 h-8 ${stat.highlight ? 'text-amber-500' : 'text-gray-400'}`} />
              {stat.highlight && <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Action needed</span>}
            </div>
            <p className="text-3xl font-light text-gray-900">
              {loading ? '...' : stat.value}
            </p>
            <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
        <div className="space-y-4">
          <div className="flex items-center gap-4 py-3 border-b border-gray-100">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <Image className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-900">New artwork added</p>
              <p className="text-xs text-gray-500">2 hours ago</p>
            </div>
          </div>
          <div className="flex items-center gap-4 py-3 border-b border-gray-100">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-900">Blog post published</p>
              <p className="text-xs text-gray-500">1 day ago</p>
            </div>
          </div>
          <div className="flex items-center gap-4 py-3">
            <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
              <FolderOpen className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-900">Collection updated</p>
              <p className="text-xs text-gray-500">3 days ago</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Artworks Content
const ArtworksContent = () => {
  const [artworks, setArtworks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingArtwork, setEditingArtwork] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [artworksData, collectionsData] = await Promise.all([
        artworksAPI.getAll(),
        collectionsAPI.getAll()
      ]);
      setArtworks(artworksData);
      setCollections(collectionsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this artwork?')) {
      try {
        await artworksAPI.delete(id);
        setArtworks(artworks.filter(a => a.id !== id));
      } catch (error) {
        console.error('Error deleting artwork:', error);
      }
    }
  };

  const handleEdit = (artwork) => {
    setEditingArtwork(artwork);
    setShowModal(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingArtwork) {
        await artworksAPI.update(editingArtwork.id, formData);
      } else {
        await artworksAPI.create(formData);
      }
      loadData();
      setShowModal(false);
      setEditingArtwork(null);
    } catch (error) {
      console.error('Error saving artwork:', error);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
          Artworks
        </h1>
        <button 
          onClick={() => { setEditingArtwork(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Artwork
        </button>
      </div>

      {/* Artworks Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Artwork</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">Collection</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {artworks.map((artwork) => {
                const collection = collections.find(c => c.id === artwork.collection_id);
                const artworkType = artwork.artwork_type || 'print';
                return (
                  <tr key={artwork.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <img src={getImageUrl(artwork.image)} alt={artwork.title} className="w-12 h-12 object-cover rounded" />
                        <span className="text-sm text-gray-900 line-clamp-1">{artwork.title}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 hidden md:table-cell">
                      {collection?.name || '-'}
                    </td>
                    <td className="px-6 py-4 hidden sm:table-cell">
                      <div className="flex items-center gap-2">
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                          artworkType === 'print' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-amber-100 text-amber-800'
                        }`}>
                          {artworkType === 'print' ? 'Print' : 'Painting'}
                        </span>
                        {artwork.sold && (
                          <span className="inline-flex px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-semibold">
                            SOLD
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {artwork.price} kr
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button 
                          onClick={() => handleEdit(artwork)}
                          className="p-2 text-gray-500 hover:text-black transition-colors"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(artwork.id)}
                          className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Add/Edit Artwork Modal */}
      {showModal && (
        <AdminModal 
          title={editingArtwork ? "Edit Artwork" : "Add New Artwork"} 
          onClose={() => { setShowModal(false); setEditingArtwork(null); }}
        >
          <ArtworkForm 
            artwork={editingArtwork}
            collections={collections}
            onSubmit={handleFormSubmit} 
          />
        </AdminModal>
      )}
    </div>
  );
};

// Collections Content
const CollectionsContent = () => {
  const [collections, setCollections] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCollections();
  }, []);

  const loadCollections = async () => {
    try {
      const data = await collectionsAPI.getAll();
      setCollections(data);
    } catch (error) {
      console.error('Error loading collections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this collection?')) {
      try {
        await collectionsAPI.delete(id);
        setCollections(collections.filter(c => c.id !== id));
      } catch (error) {
        console.error('Error deleting collection:', error);
      }
    }
  };

  const handleEdit = (collection) => {
    setEditingCollection(collection);
    setShowModal(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingCollection) {
        await collectionsAPI.update(editingCollection.id, formData);
      } else {
        await collectionsAPI.create(formData);
      }
      loadCollections();
      setShowModal(false);
      setEditingCollection(null);
    } catch (error) {
      console.error('Error saving collection:', error);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
          Collections
        </h1>
        <button 
          onClick={() => { setEditingCollection(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Collection
        </button>
      </div>

      {/* Collections Grid */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {collections.map((collection) => (
            <div key={collection.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="aspect-video">
                <img src={collection.image} alt={collection.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-4">
                <h3 className="text-lg font-medium text-gray-900 mb-1">{collection.name}</h3>
                <p className="text-sm text-gray-500 line-clamp-2 mb-4">{collection.description}</p>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handleEdit(collection)}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(collection.id)}
                    className="px-3 py-2 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Collection Modal */}
      {showModal && (
        <AdminModal 
          title={editingCollection ? "Edit Collection" : "Add New Collection"} 
          onClose={() => { setShowModal(false); setEditingCollection(null); }}
        >
          <CollectionForm 
            collection={editingCollection}
            onSubmit={handleFormSubmit} 
          />
        </AdminModal>
      )}
    </div>
  );
};

// Blog Content
const BlogContent = () => {
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const data = await blogAPI.getAll();
      setPosts(data);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogAPI.delete(id);
        setPosts(posts.filter(p => p.id !== id));
      } catch (error) {
        console.error('Error deleting post:', error);
      }
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setShowModal(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingPost) {
        await blogAPI.update(editingPost.id, formData);
      } else {
        await blogAPI.create(formData);
      }
      loadPosts();
      setShowModal(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
          Blog Posts
        </h1>
        <button 
          onClick={() => { setEditingPost(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Post
        </button>
      </div>

      {/* Blog Posts List */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading...</div>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <div key={post.id} className="bg-white rounded-lg shadow-sm p-6 flex items-start gap-6">
              {post.image && (
                <img src={post.image} alt={post.title} className="w-24 h-24 object-cover rounded hidden sm:block" />
              )}
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900 mb-1 line-clamp-1">{post.title}</h3>
                <p className="text-sm text-gray-500 mb-2">{post.date}</p>
                <p className="text-sm text-gray-600 line-clamp-2">{post.excerpt}</p>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleEdit(post)}
                  className="p-2 text-gray-500 hover:text-black transition-colors"
                >
                  <Edit className="w-4 h-4" />
                </button>
                <button 
                  onClick={() => handleDelete(post.id)}
                  className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Blog Modal */}
      {showModal && (
        <AdminModal 
          title={editingPost ? "Edit Post" : "Create New Post"} 
          onClose={() => { setShowModal(false); setEditingPost(null); }}
        >
          <BlogForm 
            post={editingPost}
            onSubmit={handleFormSubmit} 
          />
        </AdminModal>
      )}
    </div>
  );
};

// Settings Content
const SettingsContent = () => {
  const [settings, setSettings] = useState({
    site_name: '',
    tagline: '',
    announcement: '',
    announcement_link: '',
    contact_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await settingsAPI.get();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await settingsAPI.update(settings);
      alert('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center text-gray-500 py-8">Loading...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
        Settings
      </h1>

      <div className="bg-white rounded-lg shadow-sm p-6 max-w-2xl">
        <h2 className="text-lg font-medium text-gray-900 mb-6">Site Settings</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Site Name</label>
            <input
              type="text"
              value={settings.site_name}
              onChange={(e) => setSettings({...settings, site_name: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Tagline</label>
            <input
              type="text"
              value={settings.tagline}
              onChange={(e) => setSettings({...settings, tagline: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>

          {/* Announcement Bar Section */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-md font-medium text-gray-900 mb-4 flex items-center gap-2">
              📢 Announcement Bar
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              This appears at the top of your website. Leave empty to hide it.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Announcement Text</label>
                <input
                  type="text"
                  value={settings.announcement}
                  onChange={(e) => setSettings({...settings, announcement: e.target.value})}
                  placeholder="e.g., Free shipping on orders over $100!"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Link URL</label>
                <input
                  type="text"
                  value={settings.announcement_link}
                  onChange={(e) => setSettings({...settings, announcement_link: e.target.value})}
                  placeholder="e.g., /collections/new-arrivals"
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                />
                <p className="text-xs text-gray-400 mt-1">Where visitors go when they click the announcement</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6 mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
            <input
              type="email"
              value={settings.contact_email}
              onChange={(e) => setSettings({...settings, contact_email: e.target.value})}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="px-6 py-3 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>
    </div>
  );
};

// Users Content
const UsersContent = () => {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user: currentUser } = useAuth();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await usersAPI.delete(id);
        setUsers(users.filter(u => u.id !== id));
      } catch (error) {
        alert(error.response?.data?.detail || 'Error deleting user');
      }
    }
  };

  const handleResetPassword = async (userId, username) => {
    if (window.confirm(`Reset password for ${username}? A new temporary password will be generated.`)) {
      try {
        const result = await usersAPI.resetPassword(userId);
        alert(`Password reset! Temporary password: ${result.temporary_password}`);
      } catch (error) {
        alert(error.response?.data?.detail || 'Error resetting password');
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      await usersAPI.create(formData);
      loadUsers();
      setShowModal(false);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error creating user');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-light text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
          Users
        </h1>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowPasswordModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-sm rounded-md hover:bg-gray-50 transition-colors"
          >
            <Key className="w-4 h-4" />
            Change My Password
          </button>
          <button 
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add User
          </button>
        </div>
      </div>

      {/* Users List */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading...</div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 font-medium">{user.username[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user.username}</p>
                        {user.id === currentUser?.id && (
                          <span className="text-xs text-blue-600">(You)</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{user.email}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${
                      user.is_admin ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      <Shield className="w-3 h-3" />
                      {user.is_admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {user.id !== currentUser?.id && (
                        <>
                          <button 
                            onClick={() => handleResetPassword(user.id, user.username)}
                            className="p-2 text-gray-500 hover:text-orange-600 transition-colors"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => handleDelete(user.id)}
                            className="p-2 text-gray-500 hover:text-red-600 transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add User Modal */}
      {showModal && (
        <AdminModal title="Add New User" onClose={() => setShowModal(false)}>
          <UserForm onSubmit={handleFormSubmit} />
        </AdminModal>
      )}

      {/* Change Password Modal */}
      {showPasswordModal && (
        <AdminModal title="Change Your Password" onClose={() => setShowPasswordModal(false)}>
          <ChangePasswordForm onSuccess={() => setShowPasswordModal(false)} />
        </AdminModal>
      )}
    </div>
  );
};

// Modal Component
const AdminModal = ({ title, children, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-medium text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-black transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {children}
        </div>
      </div>
    </div>
  );
};

// Artwork Form
const ArtworkForm = ({ artwork, collections, onSubmit }) => {
  // Parse existing sizes - handle both legacy string array and new object format
  const parseSizes = (sizes, defaultPrice) => {
    if (!sizes || sizes.length === 0) return [];
    return sizes.map(s => {
      if (typeof s === 'string') {
        return { name: s, price: defaultPrice || 0 };
      }
      return { name: s.name || '', price: s.price || 0 };
    });
  };

  const [formData, setFormData] = useState({
    title: artwork?.title || '',
    slug: artwork?.slug || '',
    collection_id: artwork?.collection_id || (collections[0]?.id || ''),
    price: artwork?.price || '',
    currency: 'SEK', // Always SEK
    price_type: artwork?.price_type || 'from',
    artwork_type: artwork?.artwork_type || 'print',
    image: artwork?.image || '',
    hover_image: artwork?.hover_image || '',
    description: artwork?.description || '',
    in_stock: artwork?.in_stock !== false,
    sold: artwork?.sold || false
  });

  const [sizeVariants, setSizeVariants] = useState(
    parseSizes(artwork?.sizes, artwork?.price)
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    // Get lowest price as base price
    const lowestPrice = sizeVariants.length > 0 
      ? Math.min(...sizeVariants.map(s => s.price))
      : parseFloat(formData.price) || 0;

    const submitData = {
      ...formData,
      price: lowestPrice,
      currency: 'SEK',
      sizes: sizeVariants.filter(s => s.name.trim() !== '')
    };
    onSubmit(submitData);
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  const addSizeVariant = () => {
    setSizeVariants([...sizeVariants, { name: '', price: 0 }]);
  };

  const removeSizeVariant = (index) => {
    setSizeVariants(sizeVariants.filter((_, i) => i !== index));
  };

  const updateSizeVariant = (index, field, value) => {
    const updated = [...sizeVariants];
    updated[index] = { ...updated[index], [field]: field === 'price' ? parseFloat(value) || 0 : value };
    setSizeVariants(updated);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({
            ...formData, 
            title: e.target.value,
            slug: generateSlug(e.target.value)
          })}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Artwork title"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
        <input
          type="text"
          required
          value={formData.slug}
          onChange={(e) => setFormData({...formData, slug: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="artwork-slug"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Collection</label>
        <select 
          value={formData.collection_id}
          onChange={(e) => setFormData({...formData, collection_id: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
        >
          {collections.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {/* Artwork Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Artwork Type</label>
        <div className="flex gap-4">
          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-md cursor-pointer transition-all ${
            formData.artwork_type === 'print' 
              ? 'border-blue-500 bg-blue-50 text-blue-700' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="artwork_type"
              value="print"
              checked={formData.artwork_type === 'print'}
              onChange={(e) => setFormData({...formData, artwork_type: e.target.value, price_type: 'from'})}
              className="sr-only"
            />
            <span className="text-sm font-medium">Print</span>
          </label>
          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-md cursor-pointer transition-all ${
            formData.artwork_type === 'painting' 
              ? 'border-amber-500 bg-amber-50 text-amber-700' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="artwork_type"
              value="painting"
              checked={formData.artwork_type === 'painting'}
              onChange={(e) => setFormData({...formData, artwork_type: e.target.value, price_type: 'fixed'})}
              className="sr-only"
            />
            <span className="text-sm font-medium">Painting</span>
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-1">
          <strong>Prints</strong>: Can be purchased online with flat-rate shipping. 
          <strong> Paintings</strong>: Require a shipping quote.
        </p>
      </div>

      {/* SOLD Toggle - Only for Paintings */}
      {formData.artwork_type === 'painting' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.sold}
              onChange={(e) => setFormData({...formData, sold: e.target.checked})}
              className="w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
            />
            <div>
              <span className="font-medium text-amber-800">Mark as SOLD</span>
              <p className="text-xs text-amber-600 mt-0.5">
                This will display a "SOLD" label on the artwork and disable the quote request button.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* Size Variants with Prices */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Size Variants & Prices (SEK)
        </label>
        <div className="space-y-3">
          {sizeVariants.map((variant, index) => (
            <div key={index} className="flex gap-2 items-center">
              <input
                type="text"
                value={variant.name}
                onChange={(e) => updateSizeVariant(index, 'name', e.target.value)}
                placeholder="Size name (e.g., Small A3)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
              />
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">kr</span>
                <input
                  type="number"
                  step="1"
                  value={variant.price}
                  onChange={(e) => updateSizeVariant(index, 'price', e.target.value)}
                  placeholder="Price"
                  className="w-28 pl-3 pr-8 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
                />
              </div>
              <button
                type="button"
                onClick={() => removeSizeVariant(index)}
                className="p-2 text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addSizeVariant}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-black"
          >
            <Plus className="w-4 h-4" />
            Add Size Variant
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Add different sizes with their prices in EUR. Example: "Small A3" at €90, "Medium A2" at €150.
          Customers will see prices in their local currency based on location.
        </p>
      </div>

      {/* Price Type Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Price Display</label>
        <div className="flex gap-4">
          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-md cursor-pointer transition-all ${
            formData.price_type === 'from' 
              ? 'border-black bg-black text-white' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="price_type"
              value="from"
              checked={formData.price_type === 'from'}
              onChange={(e) => setFormData({...formData, price_type: e.target.value})}
              className="sr-only"
            />
            <span className="text-sm font-medium">From €XX</span>
            <span className={`text-xs ${formData.price_type === 'from' ? 'text-gray-300' : 'text-gray-400'}`}>(Prints)</span>
          </label>
          <label className={`flex-1 flex items-center justify-center gap-2 p-3 border rounded-md cursor-pointer transition-all ${
            formData.price_type === 'fixed' 
              ? 'border-black bg-black text-white' 
              : 'border-gray-300 hover:border-gray-400'
          }`}>
            <input
              type="radio"
              name="price_type"
              value="fixed"
              checked={formData.price_type === 'fixed'}
              onChange={(e) => setFormData({...formData, price_type: e.target.value})}
              className="sr-only"
            />
            <span className="text-sm font-medium">€XX</span>
            <span className={`text-xs ${formData.price_type === 'fixed' ? 'text-gray-300' : 'text-gray-400'}`}>(Paintings)</span>
          </label>
        </div>
        <p className="text-xs text-gray-400 mt-1">Choose "From" for prints with multiple size options</p>
      </div>
      
      <ImageUpload
        label="Artwork Image"
        value={formData.image}
        onChange={(url) => setFormData({...formData, image: url})}
      />

      <ImageUpload
        label="Hover Image (Optional)"
        value={formData.hover_image}
        onChange={(url) => setFormData({...formData, hover_image: url})}
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
          placeholder="Artwork description"
        />
      </div>
      <button
        type="submit"
        disabled={!formData.image}
        className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {artwork ? 'Update Artwork' : 'Add Artwork'}
      </button>
    </form>
  );
};

// Collection Form
const CollectionForm = ({ collection, onSubmit }) => {
  const [formData, setFormData] = useState({
    name: collection?.name || '',
    slug: collection?.slug || '',
    description: collection?.description || '',
    image: collection?.image || '',
    featured: collection?.featured || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
        <input
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({
            ...formData, 
            name: e.target.value,
            slug: generateSlug(e.target.value)
          })}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Collection name"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
        <input
          type="text"
          required
          value={formData.slug}
          onChange={(e) => setFormData({...formData, slug: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="collection-slug"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
        <textarea
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
          placeholder="Collection description"
        />
      </div>
      
      <ImageUpload
        label="Cover Image"
        value={formData.image}
        onChange={(url) => setFormData({...formData, image: url})}
      />

      <div className="flex items-center gap-2">
        <input 
          type="checkbox" 
          id="featured" 
          checked={formData.featured}
          onChange={(e) => setFormData({...formData, featured: e.target.checked})}
          className="rounded" 
        />
        <label htmlFor="featured" className="text-sm text-gray-700">Featured on homepage</label>
      </div>
      <button
        type="submit"
        disabled={!formData.image}
        className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {collection ? 'Update Collection' : 'Add Collection'}
      </button>
    </form>
  );
};

// Blog Form
const BlogForm = ({ post, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    image: post?.image || '',
    author: post?.author || 'Gorito'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const generateSlug = (title) => {
    return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({
            ...formData, 
            title: e.target.value,
            slug: generateSlug(e.target.value)
          })}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Post title"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Slug</label>
        <input
          type="text"
          required
          value={formData.slug}
          onChange={(e) => setFormData({...formData, slug: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="post-slug"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Excerpt</label>
        <textarea
          rows={2}
          value={formData.excerpt}
          onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
          placeholder="Short description"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
        <textarea
          rows={6}
          value={formData.content}
          onChange={(e) => setFormData({...formData, content: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black resize-none"
          placeholder="Full article content"
        />
      </div>
      
      <ImageUpload
        label="Featured Image (Optional)"
        value={formData.image}
        onChange={(url) => setFormData({...formData, image: url})}
      />

      <button
        type="submit"
        className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
      >
        {post ? 'Update Post' : 'Publish Post'}
      </button>
    </form>
  );
};

// User Form
const UserForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: ''
  });
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
        <input
          type="text"
          required
          value={formData.username}
          onChange={(e) => setFormData({...formData, username: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Enter username"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
        <input
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({...formData, email: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="user@example.com"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
        <input
          type="password"
          required
          value={formData.password}
          onChange={(e) => setFormData({...formData, password: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Min 6 characters"
        />
      </div>
      <button
        type="submit"
        className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
      >
        Create User
      </button>
    </form>
  );
};

// Change Password Form
const ChangePasswordForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await usersAPI.changePassword(formData.currentPassword, formData.newPassword);
      alert('Password changed successfully!');
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Error changing password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Current Password</label>
        <input
          type="password"
          required
          value={formData.currentPassword}
          onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Enter current password"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
        <input
          type="password"
          required
          value={formData.newPassword}
          onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Min 6 characters"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Confirm New Password</label>
        <input
          type="password"
          required
          value={formData.confirmPassword}
          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
          className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black"
          placeholder="Re-enter new password"
        />
      </div>
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
      >
        {loading ? 'Changing...' : 'Change Password'}
      </button>
    </form>
  );
};

// ============ ORDERS CONTENT ============
const OrdersContent = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const data = await ordersAPI.getAll();
      setOrders(data);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await ordersAPI.update(orderId, { status: newStatus });
      loadOrders();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'processing': return Package;
      case 'shipped': return Truck;
      case 'delivered': return CheckCircle;
      case 'cancelled': return XCircle;
      default: return Clock;
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading orders...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
        Orders
      </h1>

      {orders.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>No orders yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((order) => {
            const StatusIcon = getStatusIcon(order.status);
            return (
              <div key={order.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
                  <div>
                    <h3 className="font-medium text-lg">{order.order_number}</h3>
                    <p className="text-sm text-gray-500">
                      {order.customer_name} • {order.customer_email}
                    </p>
                    <p className="text-xs text-gray-400">
                      {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      <StatusIcon className="w-4 h-4" />
                      {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                    </span>
                    <span className="text-lg font-medium">€{order.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Order Items */}
                <div className="border-t pt-4 mb-4">
                  <div className="grid gap-3">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-4">
                        <img
                          src={getImageUrl(item.image)}
                          alt={item.title}
                          className="w-12 h-12 object-cover rounded"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{item.title}</p>
                          <p className="text-xs text-gray-500">Size: {item.size} • Qty: {item.quantity}</p>
                        </div>
                        <p className="text-sm">€{item.line_total.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Shipping Address */}
                <div className="border-t pt-4 mb-4">
                  <p className="text-sm font-medium mb-2">Shipping Address:</p>
                  <p className="text-sm text-gray-600">
                    {order.shipping_address.first_name} {order.shipping_address.last_name}<br />
                    {order.shipping_address.street}<br />
                    {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}<br />
                    {order.shipping_address.country}
                  </p>
                </div>

                {/* Order Summary */}
                <div className="border-t pt-4 mb-4 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>€{order.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    <span>€{order.shipping_cost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between font-medium text-base mt-2">
                    <span>Total:</span>
                    <span>€{order.total.toFixed(2)}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="border-t pt-4 flex flex-wrap gap-2">
                  {order.status === 'processing' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'shipped')}
                      className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm"
                    >
                      <Truck className="w-4 h-4" />
                      Mark as Shipped
                    </button>
                  )}
                  {order.status === 'shipped' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'delivered')}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Mark as Delivered
                    </button>
                  )}
                  {(order.status === 'pending' || order.status === 'processing') && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'cancelled')}
                      className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50 text-sm"
                    >
                      <XCircle className="w-4 h-4" />
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============ QUOTES CONTENT ============
const QuotesContent = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const data = await quotesAPI.getAll();
      setQuotes(data);
    } catch (error) {
      console.error('Error loading quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      await quotesAPI.update(quoteId, { status: newStatus });
      loadQuotes();
    } catch (error) {
      console.error('Error updating quote:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-amber-100 text-amber-800';
      case 'responded': return 'bg-blue-100 text-blue-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading quote requests...</div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-900 mb-8" style={{ fontFamily: 'Georgia, serif' }}>
        Shipping Quote Requests
      </h1>

      {quotes.length === 0 ? (
        <div className="bg-white rounded-lg p-8 text-center text-gray-500">
          <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-20" />
          <p>No quote requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-4">
                <div>
                  <h3 className="font-medium text-lg">{quote.artwork_title}</h3>
                  <p className="text-sm text-gray-500">
                    {quote.artwork_type === 'painting' ? 'Original Painting' : 'Print'} 
                    {quote.is_framed && ' (Framed)'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(quote.created_at).toLocaleDateString()} at {new Date(quote.created_at).toLocaleTimeString()}
                  </p>
                </div>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(quote.status)}`}>
                  {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                </span>
              </div>

              <div className="border-t pt-4 mb-4 grid md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium mb-1">Customer Details:</p>
                  <p className="text-sm text-gray-600">{quote.customer_name}</p>
                  <p className="text-sm text-gray-600">{quote.customer_email}</p>
                  <p className="text-sm text-gray-600">Shipping to: {quote.country}</p>
                </div>
                {quote.message && (
                  <div>
                    <p className="text-sm font-medium mb-1">Message:</p>
                    <p className="text-sm text-gray-600 italic">"{quote.message}"</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="border-t pt-4 flex flex-wrap gap-2">
                <a
                  href={`mailto:${quote.customer_email}?subject=Shipping Quote for ${encodeURIComponent(quote.artwork_title)}&body=Hi ${encodeURIComponent(quote.customer_name)},%0A%0AThank you for your interest in "${encodeURIComponent(quote.artwork_title)}".%0A%0AThe shipping cost to ${encodeURIComponent(quote.country)} would be: [ENTER PRICE]%0A%0ABest regards,%0AGorito Fine Arts`}
                  className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 text-sm"
                  onClick={() => updateQuoteStatus(quote.id, 'responded')}
                >
                  Reply via Email
                </a>
                {quote.status !== 'closed' && (
                  <button
                    onClick={() => updateQuoteStatus(quote.id, 'closed')}
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                  >
                    Mark as Closed
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ============ MESSAGES CONTENT ============
const MessagesContent = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const data = await messagesAPI.getAll();
      setMessages(data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await messagesAPI.updateStatus(id, status);
      setMessages(messages.map(m => m.id === id ? {...m, status} : m));
      if (selectedMessage?.id === id) {
        setSelectedMessage({...selectedMessage, status});
      }
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await messagesAPI.delete(id);
      setMessages(messages.filter(m => m.id !== id));
      if (selectedMessage?.id === id) {
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Failed to delete message:', error);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-gray-100 text-gray-800',
      replied: 'bg-green-100 text-green-800',
    };
    return styles[status] || styles.new;
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Loading messages...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">Contact Messages</h1>

      {messages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No messages yet</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Messages List */}
          <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="border-b p-4 bg-gray-50">
              <h2 className="font-medium">All Messages ({messages.length})</h2>
            </div>
            <div className="divide-y max-h-[600px] overflow-y-auto">
              {messages.map((message) => (
                <div
                  key={message.id}
                  onClick={() => {
                    setSelectedMessage(message);
                    if (message.status === 'new') {
                      updateStatus(message.id, 'read');
                    }
                  }}
                  className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedMessage?.id === message.id ? 'bg-blue-50' : ''
                  } ${message.status === 'new' ? 'bg-blue-50/50' : ''}`}
                >
                  <div className="flex justify-between items-start mb-1">
                    <div className="flex items-center gap-2">
                      {message.status === 'new' ? (
                        <Mail className="w-4 h-4 text-blue-600" />
                      ) : (
                        <MailOpen className="w-4 h-4 text-gray-400" />
                      )}
                      <span className="font-medium text-sm">{message.name}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusBadge(message.status)}`}>
                      {message.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 truncate">{message.subject}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatDate(message.created_at)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Message Detail */}
          <div className="bg-white rounded-lg shadow-sm border">
            {selectedMessage ? (
              <div className="p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h2 className="text-lg font-semibold">{selectedMessage.subject}</h2>
                    <p className="text-sm text-gray-500 mt-1">
                      From: <span className="font-medium">{selectedMessage.name}</span>
                    </p>
                    <a 
                      href={`mailto:${selectedMessage.email}`}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      {selectedMessage.email}
                    </a>
                  </div>
                  <button
                    onClick={() => deleteMessage(selectedMessage.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Delete message"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedMessage.message}</p>
                </div>

                <div className="text-xs text-gray-400 mb-4">
                  Received: {formatDate(selectedMessage.created_at)}
                </div>

                <div className="flex gap-2">
                  <a
                    href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                    className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 text-sm"
                    onClick={() => updateStatus(selectedMessage.id, 'replied')}
                  >
                    <Mail className="w-4 h-4" />
                    Reply via Email
                  </a>
                  {selectedMessage.status !== 'replied' && (
                    <button
                      onClick={() => updateStatus(selectedMessage.id, 'replied')}
                      className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                    >
                      Mark as Replied
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="p-6 text-center text-gray-500 h-full flex items-center justify-center">
                <div>
                  <Mail className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p>Select a message to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
